import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useRef, useState } from 'react';

/**
 * FE-C — reservation FE-timer (Wave-1, Am-4 D2: csak FE-vizuális, NINCS BE-lock).
 *
 * ⚠️ Szándékosan NEM valódi készlet-lock — vizuális emlékeztető a pultosnak,
 * hogy időben véglegesítse a bérlést. A copy NEM ígér lockot; az oversell-védelem
 * a Phase-2 BE reservation-lock-ra hárul (roadmap FE-C AC2, NotebookLM-evidencia:
 * a FE-only timer reset-on-reload + read-then-write race → ne ígérj garanciát).
 *
 * Design: neutrális (nem villogó-piros); lejáratkor higgadt „lejárt" állapot.
 */
export function ReservationTimer({
  durationSec = 900,
  className,
}: {
  durationSec?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(durationSec);
  const startedRef = useRef<number | null>(null);

  useEffect(() => {
    // monoton számláló: 1mp-enként csökkent, 0-nál megáll
    const id = setInterval(() => {
      setRemaining(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  // referencia a lint-hez (induló érték rögzítése, NEM Date.now — resume-safe)
  if (startedRef.current === null) startedRef.current = durationSec;

  const expired = remaining <= 0;
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const formatted = `${mm}:${String(ss).padStart(2, '0')}`;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
        expired
          ? 'border-border bg-muted text-muted-foreground'
          : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
        className
      )}
      data-testid="reservation-timer"
      role="status"
      aria-live="polite"
    >
      <svg
        className="h-4 w-4 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <circle cx="12" cy="13" r="8" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M12 9v4l2 2M9 2h6" />
      </svg>
      {expired ? (
        <span data-testid="reservation-timer-expired">
          {t('rental.wizard.reservationTimer.expired')}
        </span>
      ) : (
        <span>
          {t('rental.wizard.reservationTimer.label')}{' '}
          <span className="font-semibold tabular-nums" data-testid="reservation-timer-value">
            {formatted}
          </span>
        </span>
      )}
      <span className="ml-auto text-xs text-muted-foreground">
        {t('rental.wizard.reservationTimer.hint')}
      </span>
    </div>
  );
}
